import React, { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { uploadDocument, validateFile, formatFileSize, DocumentType } from '../../lib/documentService';

interface DocumentUploadProps {
  userId: string;
  documentType: DocumentType;
  onUploadComplete: (url: string) => void;
  onCancel?: () => void;
  accept?: string;
  maxSizeMB?: number;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  userId,
  documentType,
  onUploadComplete,
  onCancel,
  accept = '.pdf,.jpg,.jpeg,.png,.webp',
  maxSizeMB = 10
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    setError(null);
    const validation = validateFile(file);

    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    const result = await uploadDocument(selectedFile, userId, documentType);

    if (result.success && result.url) {
      setSuccess(true);
      setTimeout(() => {
        onUploadComplete(result.url!);
      }, 1000);
    } else {
      setError(result.error || 'Upload failed');
    }

    setUploading(false);
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {!selectedFile ? (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 hover:border-slate-400'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleInputChange}
            accept={accept}
            className="hidden"
          />

          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              dragActive ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-slate-400'}`} />
            </div>

            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Upload Document
            </h3>

            <p className="text-sm text-slate-600 mb-4">
              Drag and drop your file here, or click to browse
            </p>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-colors"
            >
              Choose File
            </button>

            <p className="text-xs text-slate-500 mt-4">
              Supported formats: PDF, JPG, PNG, WebP (max {maxSizeMB}MB)
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3 flex-1">
              <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <File className="w-6 h-6 text-slate-600" />
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 truncate">
                  {selectedFile.name}
                </h4>
                <p className="text-sm text-slate-600">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>

            {!uploading && !success && (
              <button
                onClick={handleRemove}
                className="p-1 hover:bg-slate-200 rounded transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            )}
          </div>

          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700">Upload successful!</p>
            </div>
          )}

          {!success && (
            <div className="flex space-x-3">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </button>

              {onCancel && (
                <button
                  onClick={onCancel}
                  disabled={uploading}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
